// Общите парчета на таблиците на справките: заглавие на колона (сортира и
// носи филтъра на колоната — `components/filters/ColumnHeader`), сума в реда
// „Филтри: …" над таблицата, „Покажи още", празен резултат от филтрите,
// зареждане и тирето за празна стойност. Логиката (търсене, сортиране, ↑/↓)
// е в `reportListModel.ts`, филтрите — в `components/filters/`.
import { Spin } from "antd";
import { FilterOutlined } from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import { EmptyScreen } from "../../components/list/ListBits";
import { LINK_BUTTON_CLASS, SECONDARY_BUTTON_CLASS } from "../../components/list/buttonStyles";
import { ColumnHeader } from "../../components/filters/ColumnHeader";
import type { ColumnFilterSection } from "../../components/filters/useColumnFilters";
import type { ReportSort } from "./reportListModel";

// ─── Заглавие на колона: сортира и/или носи филтъра на колоната ──────────
type ReportHeadProps<K extends string> = {
  label: string;
  sortKey?: K;
  sort?: ReportSort<K> | null;
  onSort?: (key: K) => void;
  align?: "left" | "right";
  filters?: ColumnFilterSection[];
  wrap?: boolean; // дълъг етикет — на два реда вместо „…"
};

export function ReportHead<K extends string>({
  label,
  sortKey,
  sort,
  onSort,
  align,
  filters,
  wrap,
}: ReportHeadProps<K>) {
  return (
    <ColumnHeader
      label={label}
      align={align}
      sortDir={sortKey && sort?.key === sortKey ? sort.dir : null}
      onSort={sortKey && onSort ? () => onSort(sortKey) : undefined}
      filters={filters}
      wrap={wrap}
    />
  );
}

// Сума в реда над таблицата: „Бруто 1 234.56"
export const ResultSum = ({ label, value, strong = false }: { label: string; value: string; strong?: boolean }) => (
  <span>
    {label}{" "}
    <span className={`font-sheet font-bold ${strong ? "text-brand-deep-ink" : "text-ink"}`}>{value}</span>
  </span>
);

// „›" в края на ред/карта, който се отваря — без него редът подсказваше само с
// курсора и hover-а (на телефон — с нищо). Декоративен: редът е role="button".
export const OpenChevron = () => (
  <span aria-hidden className="shrink-0 text-right text-[17px] leading-none text-muted">
    ›
  </span>
);

// ─── „Покажи още (N)" под редовете ────────────────────────────────────────
export const ShowMoreRow = ({ hiddenCount, onShowMore }: { hiddenCount: number; onShowMore: () => void }) => {
  const { t } = useTranslation();
  if (hiddenCount <= 0) return null;
  return (
    <div className="border-t border-shade-1 px-[18px] py-2 text-center">
      <button type="button" onClick={onShowMore} className={LINK_BUTTON_CLASS}>
        {t("inventoryPage.showMore", { count: hiddenCount })}
      </button>
    </div>
  );
};

// Периодът има данни, но търсенето и филтрите не оставят нищо. Стои ПОД
// заглавията на таблицата — фуниите остават достъпни.
export const ReportNoMatch = ({ onClear }: { onClear: () => void }) => {
  const { t } = useTranslation();
  return (
    <EmptyScreen
      Icon={FilterOutlined}
      tone="brand"
      title={t("inventoryPage.cfNoMatch")}
      action={
        <button type="button" onClick={onClear} className={SECONDARY_BUTTON_CLASS}>
          {t("inventoryPage.cfClearFilters")}
        </button>
      }
    />
  );
};

// Зареждане на цялата справка
export const ReportLoading = () => (
  <div className="flex justify-center p-12">
    <Spin size="large" />
  </div>
);

// Нулата е шум в справката — празната клетка се чете като тире
export const EmptyDash = () => <span className="text-faint">—</span>;
